import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";

// CSV: scenario_code,portfolio_code,run_date,pnl_pct[,notes]
export default function StressResultsUploader() {
  const [csv, setCsv] = useState("");
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState(0);
  const { toast } = useToast();

  const loadStats = async () => {
    const { count } = await supabase
      .from("policy_stress_results" as any)
      .select("*", { count: "exact", head: true });
    setCount(count ?? 0);
  };
  useEffect(() => { loadStats(); }, []);

  const upload = async () => {
    setBusy(true);
    try {
      const lines = csv.trim().split(/\r?\n/).filter(l => l.trim());
      if (!lines.length) throw new Error("No rows.");
      const data = lines[0].toLowerCase().includes("scenario_code") ? lines.slice(1) : lines;

      // Resolve scenario_code -> id
      const { data: scenarios, error: sErr } = await supabase
        .from("policy_stress_scenarios" as any)
        .select("id,scenario_code");
      if (sErr) throw sErr;
      const scenMap = new Map((scenarios ?? []).map((s: any) => [s.scenario_code, s.id]));

      const rows = data.map(l => {
        const [code, portfolio_code, run_date, pnlRaw, ...rest] = l.split(",").map(s => s.trim());
        const pnl_pct = Number(pnlRaw);
        const scenario_id = scenMap.get(code);
        if (!scenario_id) throw new Error(`Unknown scenario_code: ${code}`);
        if (!portfolio_code || !run_date || !Number.isFinite(pnl_pct)) throw new Error(`Bad row: ${l}`);
        return { scenario_id, portfolio_code, run_date, pnl_pct, notes: rest.join(",") || null };
      });

      const { error } = await supabase
        .from("policy_stress_results" as any)
        .upsert(rows, { onConflict: "scenario_id,portfolio_code,run_date" });
      if (error) throw error;

      toast({ title: "Upload complete", description: `${rows.length} results upserted.` });
      setCsv("");
      loadStats();
    } catch (e: any) {
      toast({ title: "Upload failed", description: e.message ?? String(e), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader><CardTitle>§13 Stress Test Results</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="text-xs text-muted-foreground space-y-1">
          <p>Computed P&amp;L impact per portfolio for each scenario. Reference scenarios by <code>scenario_code</code> from the library above.</p>
          <p><strong>CSV:</strong> <code>scenario_code,portfolio_code,run_date,pnl_pct[,notes]</code></p>
          <p><strong>pnl_pct</strong> as decimal, e.g. -0.18 for -18%.</p>
        </div>
        <div className="text-xs border rounded p-3 bg-muted/30">
          <span className="text-muted-foreground">Result rows:</span>{" "}
          <span className="font-medium tabular-nums">{count.toLocaleString()}</span>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Paste CSV</Label>
          <Textarea
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
            placeholder={"scenario_code,portfolio_code,run_date,pnl_pct,notes\nGFC2008,P3,2026-05-15,-0.18,Re-run with current weights"}
            rows={8}
            className="font-mono text-xs"
          />
        </div>
        <div className="flex gap-2">
          <Button onClick={upload} disabled={busy || !csv.trim()}>{busy ? "Uploading…" : "Upload"}</Button>
          <Button variant="outline" onClick={() => setCsv("")} disabled={busy}>Clear</Button>
        </div>
      </CardContent>
    </Card>
  );
}
